<template>
  <layout name="Sidebar" extraClass="contact-detail__layout">
    <WithLoader
      :isLoading="isLoading || !contact.id"
      classType="primary"
      fullHeight
    >
      <div class="contact-detail__outer">
        <div class="contact-detail__heading">
          <div class="account-profile-outer">
            <AccountProfileHeading
              :firstName="contact.first_name"
              :lastName="contact.last_name"
              :verification="contact.verification"
              :city="contact.address_city"
              :country="contact.address_country"
              :deliverable="!isAddressUndeliverable"
            />
          </div>

          <div class="contacts-info-block__map">
            <VMap
              :latitude="latitude"
              :longitude="longitude"
              :notDeriverable="isAddressUndeliverable"
            />
          </div>

          <div class="contacts-info-block__buttons-wrapper">
            <VContextMenu
              :actions="getActions()"
              type="round"
              dpSize="wide"
              noBorder
            />
          </div>
        </div>

        <WithLoader :isLoading="!contact" classType="primary">
          <div
            class="contact-detail__wrap"
            :class="{
              ['no-activity']: noActivity,
              ['table-activity']: _activityView === 'table'
            }"
          >
            <div class="left">
              <div class="details__wrapper">
                <h3>Details</h3>
                <VExpandableBlock
                  header="Contact"
                  extraClass="contact-details-expand"
                  expandOnLoad
                  notExpandable
                >
                  <ContactsInfoBlock
                    :id="contact.id"
                    :firstName="contact.first_name"
                    :lastName="contact.last_name"
                    :city="contact.address_city"
                    :unit="contact.address_line_2"
                    :email="contact.email"
                    :state="contact.address_state"
                    :zip="contact.address_zip"
                    :country="contact.address_country"
                    :street="contact.address_line_1"
                    :created="contact.created_at"
                    :modified="contact.updated_at"
                    :phone="contact.phone"
                    :company="contact.company"
                    :source="contact.source"
                    :deliverability="
                      contact.verification &&
                        contact.verification.verification_status
                    "
                    :tags="getTags"
                    :customData="formattedCustomData(contact.custom_data)"
                  />
                </VExpandableBlock>
              </div>
              <div class="details__wrapper custom-left">
                <VExpandableBlock
                  header="Custom Data"
                  extraClass="contact-details-expand"
                >
                  <div class="item custom" v-if="customDataExists">
                    <div
                      class="custom-data"
                      v-for="(data, idx) in custom"
                      :key="idx"
                    >
                      <VEditableText
                        _key="address"
                        :value="data.key.value"
                        addPlaceholder="Add Key"
                        @change="
                          (key, val) => onBlurCustomData(idx, 'key', val)
                        "
                        extraClass="key"
                        hideLabel
                        hideIcon
                      />

                      <VEditableText
                        _key="address1"
                        :value="data.value.value"
                        addPlaceholder="Add Value"
                        @change="
                          (key, val) => onBlurCustomData(idx, 'value', val)
                        "
                        extraClass="custom-value"
                        hideLabel
                        hideIcon
                      />
                    </div>
                  </div>
                  <div v-else-if="!customDataExists" class="item no-data">
                    No data
                  </div>

                  <div class="expand custom-data-expand" @click="addNewCustom">
                    Add custom data
                  </div>
                </VExpandableBlock>
              </div>

              <div class="details__wrapper" v-if="contact.verification">
                <VExpandableBlock
                  header="Address Enrichment"
                  extraClass="contact-details-expand"
                  expandOnLoad
                >
                  <div class="item">
                    <div class="title-wrapper">
                      <span class="no-hint"> Deliverable </span>
                    </div>
                    <span class="value">
                      {{ deliverableStatus }}
                    </span>
                  </div>

                  <div
                    v-for="(key, idx) in ENRICHMENT_ORDER"
                    class="item"
                    :key="idx"
                    :set="(capitalizedKey = replaceAndCapitalize(key))"
                    :title="contact.verification.details[key]"
                    v-show="expanded || (!expanded && idx < 7)"
                  >
                    <div
                      class="title-wrapper"
                      v-if="contact.verification.details[key]"
                    >
                      <span class="title">
                        {{ _getEnrichmentTitle(key) || capitalizedKey }}</span
                      >

                      <div class="hint-wrapper" v-if="HINTS[key]">
                        <VHint
                          :text="HINTS[key]"
                          theme="dark"
                          extraClass="contact-details-hint"
                        ></VHint>
                      </div>
                    </div>
                    <span
                      v-if="contact.verification.details[key]"
                      class="value"
                      >{{ contact.verification.details[key] }}</span
                    >
                  </div>

                  <div
                    v-if="enrichmentMoreCount"
                    class="expand"
                    @click="toggleExpand"
                  >
                    Show {{ expanded ? "less" : `${enrichmentMoreCount} more` }}
                  </div>
                </VExpandableBlock>
              </div>
            </div>

            <div class="center">
              <ContactsDetailsActivitySection :contact="contact" />
            </div>

            <div class="right">
              <div class="details__wrapper">
                <VExpandableBlock
                  header="Custom Data"
                  extraClass="contact-details-expand"
                >
                  <div class="item custom" v-if="customDataExists">
                    <div
                      class="custom-data"
                      v-for="(data, idx) in custom"
                      :key="idx"
                    >
                      <VEditableText
                        _key="address"
                        :value="data.key.value"
                        addPlaceholder="Add Key"
                        @change="
                          (key, val) => onBlurCustomData(idx, 'key', val)
                        "
                        extraClass="key"
                        hideLabel
                        hideIcon
                      />

                      <VEditableText
                        _key="address1"
                        :value="data.value.value"
                        addPlaceholder="Add Value"
                        @change="
                          (key, val) => onBlurCustomData(idx, 'value', val)
                        "
                        hideLabel
                        hideIcon
                      />
                    </div>
                  </div>
                  <div v-else-if="!customDataExists" class="item no-data">
                    No data
                  </div>

                  <div class="expand custom-data-expand" @click="addNewCustom">
                    Add custom data
                  </div>
                </VExpandableBlock>
              </div>
            </div>
            <VConfirmModal
              :isOpened="deleteContactConfirmOpened"
              @approve="_deleteContact"
              @cancel="deleteContactConfirmOpened = false"
            >
              <div class="confirm-message">
                Deleting this contact?
              </div>
            </VConfirmModal>
          </div>
        </WithLoader>
      </div>
    </WithLoader>
  </layout>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import omit from "lodash/omit";
import { mapActions, mapGetters } from "vuex";

import Layout from "@/layouts/Layout";

// components
import WithLoader from "@/components/WithLoader";
import VConfirmModal from "@/components/VConfirmModal";
import ContactsInfoBlock from "@/components/ContactsInfoBlock";
import VExpandableBlock from "@/components/VExpandableBlock";
import VMap from "@/components/VMap";
import AccountProfileHeading from "@/components/AccountProfileHeading";
import VHint from "@/components/VHint";
import VContextMenu from "@/components/VContextMenu";
import VEditableText from "@/components/VEditableText";
import ContactsDetailsActivitySection from "@/components/ContactsDetailsActivitySection";

// mixins
import withNavigate from "@/mixins/withNavigate";
import withToasted from "@/mixins/withToasted";
import withExpand from "@/mixins/withExpand";

// utils
import {
  fromObjectTo2DimArray,
  replaceAndCapitalize,
  generateUUID
} from "@/utils/util";

// contants
import {
  ENRICHMENT_HINTS as HINTS,
  ENRICHMENT_MAP,
  ENRICHMENT_ORDER
} from "@/constants/common";

export default Vue.component("ContactsDetails", {
  metaInfo: {
    title: "Contact Details"
  },

  components: {
    Layout,
    WithLoader,
    VConfirmModal,
    ContactsInfoBlock,
    VExpandableBlock,
    VMap,
    AccountProfileHeading,
    VHint,
    VContextMenu,
    VEditableText,
    ContactsDetailsActivitySection
  },

  data() {
    return {
      id: null,
      deleteContactConfirmOpened: false,
      editContactOpened: false,
      custom: {},
      latitude: null,
      longitude: null,
      HINTS,
      ENRICHMENT_ORDER
    };
  },

  mixins: [withNavigate, withToasted, withExpand],

  computed: {
    _activityView() {
      return this.activityView;
    },

    contact() {
      return this.getContact(this.id) || {};
    },

    customDataExists() {
      if (!this.custom) {
        return false;
      }

      return !!Object.keys(this.custom).length;
    },

    enrichmentMoreCount() {
      if (
        !this.contact.verification ||
        !this.contact.verification.verification_status ||
        this.contact.verification.verification_status === "N"
      ) {
        return "";
      }

      const objKeysNum = 21;
      return objKeysNum > 7 ? objKeysNum - 7 : "";
    },

    noActivity() {
      return this.contactActivities && !this.contactActivities.length;
    },

    getTags() {
      if (!this.contact && !this.contact.tags) {
        return [];
      }

      if (!this.contact.tags) {
        return [];
      }

      return (
        this.contact.tags
          .map(tag => this.getTag(tag))
          //termporary filter for deleted tags
          .filter(tag => tag && tag.id)
      );
    },

    tagsString() {
      if (!this.getTags.length) {
        return "";
      }
      return this.getTags.map(tag => tag && tag.name).join(", ");
    },

    usValidation() {
      if (this.getUser.us_validation) {
        return this.getUser.us_validation;
      }
    },

    isAddressUndeliverable() {
      if (this.contact.verification) {
        return (
          (this.usValidation === "strict" &&
            this.contact.verification.status !== "Y") ||
          ((this.usValidation === "off" || this.usValidation === "medium") &&
            this.contact.verification.status === "N")
        );
      }
    },

    deliverableStatus() {
      if (!this.usValidation) {
        return "N/A";
      }

      return this.isAddressUndeliverable ? "No" : "Yes";
    },

    ...mapGetters("contacts", [
      "getTag",
      "getContact",
      "isLoading",
      "mailPieces",
      "tags",
      "activityView",
      "mailPiecesLoading",
      "contactActivities"
    ]),

    ...mapGetters("campaigns", ["getCampaign"]),
    ...mapGetters("account", ["getUser"])
  },

  watch: {
    contact: function(value) {
      this.custom = this._formatInitialCustomData(value.custom_data);
      this.latitude =
        value &&
        value.verification &&
        value.verification.details &&
        Number(value.verification.details.latitude);
      this.longitude =
        value &&
        value.verification &&
        value.verification.details &&
        Number(value.verification.details.longitude);
    }
  },

  methods: {
    getCampaignLink(id) {
      return `/campaigns/${id}`;
    },

    changeCustomData(idx, key, val) {
      this.custom[idx] = {
        ...this.custom[idx],
        [key]: {
          touched: true,
          value: val
        }
      };
    },

    _getEnrichmentTitle(key) {
      return ENRICHMENT_MAP[key];
    },

    onBlurCustomData(idx, key, val) {
      // avoid duplication of custom data keys
      const customDataVal = this.contact.custom_data[val];
      if (
        key === "key" &&
        customDataVal &&
        this.custom[idx]["value"].value !== customDataVal
      ) {
        this.toast(
          `Can't create custom data with key -> ${val}. Already exists.`,
          "error"
        );
        this.custom = omit(this.custom, idx);
        return;
      }

      this.changeCustomData(idx, key, val);
      const { key: k, value: v } = this.custom[idx];

      if (k.touched && v.touched) {
        // save full or empty custom data pair
        if ((k.value && v.value) || (!k.value && !v.value)) {
          let obj = {};
          for (let i of Object.keys(this.custom)) {
            const { key, value } = this.custom[i];

            // avoid empty
            if (key.value && value.value) {
              obj[key.value] = value.value;
            }
          }

          this.editContact({
            data: {
              id: this.contact.id,
              custom_data: { ...obj, [k.value]: v.value }
            },
            onSuccess: () => {
              this.toast("Custom data fields have been saved!");
              if (
                (!k.value && !v.value) ||
                (this.custom[idx] && k.value && v.value)
              ) {
                this.custom = omit(this.custom, idx);
              }
            },
            onError: () => {
              this.toast("Something went wrong!", "error");
            }
          });
        }
      }
    },

    addNewCustom() {
      this.custom = {
        ...this.custom,
        [generateUUID()]: {
          key: {
            touched: false,
            value: ""
          },
          value: {
            touched: false,
            value: ""
          }
        }
      };
    },

    replaceAndCapitalize(str) {
      return replaceAndCapitalize(str);
    },

    getActions() {
      return [
        {
          title: "Delete",
          extraClass: "remove",
          icon: "trash-o",
          description: "Deletes this contact and their activity history",
          handler: e => this._handleDeleteContact(e)
        }
      ];
    },

    getTemplateLink(id) {
      return `/templates/builder/${id}`;
    },

    formattedCustomData(data) {
      if (!data) {
        return [];
      }

      return fromObjectTo2DimArray(data);
    },

    _formatInitialCustomData(data) {
      const output = {};

      for (let c in data) {
        output[generateUUID()] = {
          key: {
            touched: true,
            value: c
          },
          value: {
            touched: true,
            value: data[c]
          }
        };
      }

      return output;
    },

    _deleteContact() {
      this.deleteContact({
        id: this.id,
        onSuccess: () => {
          this.navigate("/contacts");
          this.toast("Contact has been deleted successfully.");
          this.deleteContactConfirmOpened = false;
        },

        onError: () => {
          this.toast(
            "Failed to delete contact. Please, try again later.",
            "error"
          );
        }
      });
    },

    _handleDeleteContact(e) {
      e.stopPropagation();
      this.deleteContactConfirmOpened = true;
    },

    getTemplate(piece) {
      if (piece.template) {
        const { name, id } = piece.template;
        return { id, name };
      }

      if (!piece.campaign) {
        return null;
      }

      const camp = this.getCampaign(piece.campaign.id);

      if (camp && camp.deleted_at) {
        return null;
      }

      const { template } = camp.mailers[0];

      if (!template) {
        return null;
      }

      const { name, id } = template;
      return { name, id };
    },

    mailed(date) {
      return moment(date).format("dddd, MMMM Do, YYYY");
    },

    ...mapActions("contacts", [
      "fetchContact",
      "fetchContactDetailsTagsList",
      "deleteContact",
      "editContact"
    ]),

    ...mapActions("account", ["fetchActivities"])
  },

  mounted() {
    this.id = this.$route.params.id;

    if (!this.id) {
      this.navigate("/contacts");
    }

    if (!this.isLoading) {
      this.fetchContact({
        id: this.id,
        onSuccess: contact => {
          /* fetch tags for contact details page
           * (temporary solution)
           */
          const tagsString = `&id=${contact.tags.join(",")}`;
          this.fetchContactDetailsTagsList({
            queryString: tagsString
          });
        },
        onError: e => {
          this.navigate("/contacts");
          this.toast("Contact not found", "error");
        }
      });
    }
  }
});
</script>

<style lang="scss" scoped>
$ns: "contact-detail";

.contacts-info-block {
  &__buttons-wrapper {
    padding-top: 120px;
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    position: relative;
    z-index: 999;

    .menu-icon {
      cursor: pointer;
    }
  }

  &__map {
    width: 100%;
    height: 100%;
  }
}

.account-profile-outer {
  padding-top: 80px;
  width: 100%;
  position: relative;
  z-index: 2;
}

.#{$ns} {
  &__outer {
    height: calc(100% - 70px);

    .expand {
      font-size: 15px;
      color: #acacac;
      cursor: pointer;
      margin-top: 21px;
      font-family: "Lato-Medium", Arial, sans-serif;
      font-weight: 500;
      transition: color 0.1s linear;

      &.custom-data-expand {
        padding-left: 3px;
      }

      &:hover {
        color: $primary-color;
      }
    }
  }

  &__heading {
    height: 186px;
    background-color: #f6f8f9;
    padding: 0 10px 0 40px;
    display: flex;
    justify-content: space-around;
    position: relative;
    border-bottom: 1px solid #d8d8d8;

    button.sm {
      margin-right: 10px;

      &:last-child {
        margin-right: 0;
      }
    }

    @media screen and (max-width: $tablet-screen) {
      @include textCropEllipsis();

      padding: 30px;
      height: auto;
      position: relative;
      z-index: 0;

      .account-profile-outer {
        padding-top: 60px;
      }
    }

    @media screen and (max-width: $small-screen) {
      padding: 20px;
    }
  }

  &__wrap {
    width: 100%;
    color: $secondary-text-color;
    display: flex;
    margin-bottom: 10px;
    font-size: 14px;
    justify-content: space-between;
    padding: 25px 30px;

    h3 {
      color: $primary-text-color;
      margin-bottom: 17px;
      padding-left: 10px;
      font-size: 22px;
      font-family: "Lato-Regular", Arial, sans-serif;
    }

    .center {
      width: 50%;
      min-width: 50%;
      max-width: 50%;
      flex: 2 2 50%;
      padding: 0 35px;

      @media screen and (min-width: $tablet-screen) and (max-width: $medium-screen) {
        padding-right: 0;
      }

      @media screen and (min-width: 1500px) and (max-width: $extra-large-screen) {
        padding: 0 5%;
      }

      @media screen and (min-width: $extra-large-screen) {
        padding: 0 10%;
      }
    }

    .right {
      max-width: 450px;
      padding-top: 39px;
    }

    .left,
    .right {
      width: 25%;
      min-width: 25%;
      max-width: 25%;
      flex: 1 1 25%;
    }

    .details__wrapper {
      margin-bottom: 20px;

      &:last-child {
        margin-bottom: 0;
      }

      .item {
        color: $secondary-text-color;
        font-size: 14px;
        line-height: 2.7;
        display: flex;
        align-items: center;

        &.no-data {
          padding-left: 3px;
        }

        .title-wrapper {
          display: flex;
          align-items: center;
          width: 155px;
          min-width: 155px;

          .hint-wrapper {
            position: relative;
            margin-right: 10px;
          }
        }

        .dots {
          margin-right: 5px;
        }

        .custom-data {
          display: flex;
          align-items: center;
          width: 100%;

          .v-editable-text__wrapper {
            max-width: 50%;
          }

          &-input {
            padding: 2px;
            margin-top: 10px;
            color: $default-color;
            width: 72px;
            min-width: 72px;
            border: 1px solid transparent;
            outline: none;
            font-size: 14px;

            &:first-child {
              margin-right: 33px;
            }

            &:last-child {
              color: $secondary-text-color;
              width: 200px;
            }

            &::placeholder {
              color: $default-color;
            }

            &:hover,
            &:focus {
              border-color: #b6b6ff;
              box-shadow: 0 0 4px rgba(87, 87, 245, 0.14);
            }
          }
        }

        span:first-child {
          display: inline-block;
          color: $default-color;
          margin-right: 5px;
          white-space: nowrap;
          line-height: 2;
          padding-left: 3px;

          &.no-hint {
            /*width: 117px;*/
          }
        }

        .value {
          @include textCropEllipsis();
          width: 50%;
        }

        &.address .value {
          white-space: normal;
        }

        &.custom {
          flex-direction: column;
          align-items: flex-start;
        }
      }
    }

    .vert-hr {
      width: 3px;
      margin: 0 30px 0 50px;
      background-color: $button-dark-color;
    }

    &.no-activity {
      .center {
        width: 75%;
        min-width: 75%;
        max-width: 75%;
        flex: 2 2 75%;
        padding: 0 35px;

        @media screen and (min-width: $tablet-screen) and (max-width: $medium-screen) {
          padding-right: 0;
        }

        @media screen and (min-width: 1500px) and (max-width: $extra-large-screen) {
          padding: 0 5%;
        }

        @media screen and (min-width: $extra-large-screen) {
          padding: 0 10%;
        }
      }

      .right {
        display: none;
      }

      .custom-left {
        display: block;
      }

      &.table-activity {
        .center {
          padding: 0 35px;
        }
      }
    }

    &.table-activity {
      .center {
        width: 75%;
        min-width: 75%;
        max-width: 75%;
        flex: 2 2 75%;
        padding-right: 0;

        @media screen and (min-width: 1500px) and (max-width: $extra-large-screen) {
          padding: 0 0 0 5%;
        }

        @media screen and (max-width: $tablet-screen) {
          width: 100%;
          min-width: 100%;
          max-width: 100%;
          flex: 2 2 100%;
        }
      }
    }
  }

  &__pieces-wrapper {
    display: flex;
    flex-direction: column;

    h3 {
      font-size: 18px;
      font-weight: 700;
      margin-bottom: 40px;
      margin-left: 20px;
    }
  }
}

@media screen and (min-width: $medium-screen) {
  .custom-left {
    display: none;
  }

  .#{$ns}__wrap.table-activity {
    .custom-left {
      display: block;
    }

    .right {
      display: none;
    }
  }
}

@media screen and (max-width: $medium-screen) {
  .#{$ns}__wrap {
    .right {
      display: none;
    }

    .left {
      width: 40%;
      min-width: 40%;
      max-width: 40%;
      flex: 1 1 40%;
    }

    .center,
    &.table-activity .center,
    &.no-activity .center {
      width: 60%;
      min-width: 60%;
      max-width: 60%;
      flex: 1 1 60%;
    }
  }
}

@media screen and (max-width: $tablet-screen) {
  .contacts-info-block {
    &__name {
      .name {
        width: 100%;
        white-space: initial;
      }
    }

    &__buttons-wrapper {
      justify-content: flex-start;
      padding-top: 52px;
    }
  }

  .#{$ns} {
    &__wrap {
      align-items: center;
      flex-direction: column;

      .left {
        width: 100%;
      }

      &.table-activity .center,
      &.no-activity .center,
      .left,
      .right,
      .center {
        width: 100%;
        min-width: 100%;
        max-width: 100%;
        flex: 1 1 100%;
      }

      .details__wrapper {
        width: 100%;
        min-width: auto;
      }

      &.no-activity .center,
      .center {
        margin-top: 25px;
        padding: 0;
      }
    }

    &__pieces-wrapper {
      margin-left: 0;
      margin-top: 50px;
    }
  }
}
</style>

<style lang="scss">
$ns: "contact-detail";

.#{$ns} {
  &__pieces-table-wrapper {
    max-height: calc(100vh - 180px);
    overflow: auto;
  }
}

div.contact-detail__layout .sidebar-layout__content {
  &,
  .sidebar-layout__content-wrapper {
    padding: 0;
  }
}

.details__wrapper {
  .contact-details-expand {
    border-radius: 4px;
    margin-bottom: 0;
  }
}

div.v-editable-text__wrapper.key {
  div.value-placeholder {
    width: 150px;
    max-width: 150px;
  }
  div.no-val,
  input {
    @include textCropEllipsis();
    color: $default-color;
    width: 100px;
    max-width: 100px;
  }
}

div.v-editable-text__wrapper {
  width: 100%;

  &:first-child {
    margin-right: 10px;
  }

  &.key,
  &.custom-value {
    max-width: 50%;
  }
}

.v-context-menu__wrapper.round .menu-wrapper li.remove {
  &:hover {
    .summary {
      .description {
        color: $primary-color;
      }
    }
  }
}

.contact-details-hint {
  [tooltip]::after {
    font-size: 11px;
    width: 180px;
  }
}
</style>
